/**
 * Hook for subscribing to real-time feature flag updates via SSE.
 *
 * Connects to the /api/feature-flags/stream endpoint and invalidates
 * TanStack Query cache when flag changes are received.
 *
 * @see specs/feature-flags-system.md
 */
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useCallback } from "react"
import { flagQueryKeys } from "@/lib/queryKeys"

/** Reconnection backoff starting delay (ms) */
const INITIAL_RECONNECT_DELAY_MS = 1_000

/** Maximum reconnection delay (ms) */
const MAX_RECONNECT_DELAY_MS = 30_000

/** Backoff multiplier */
const BACKOFF_MULTIPLIER = 2

export type FlagUpdateEvent = {
  flags: Array<{
    key: string
    action: string
    timestamp: string
  }>
  environment: string
  timestamp: string
}

export type UseFlagStreamOptions = {
  /** Called when connection is established */
  onConnected?: (data: { environment: string; userId: string; timestamp: string }) => void
  /** Called when flag updates are received */
  onFlagUpdate?: (event: FlagUpdateEvent) => void
  /** Called on connection error */
  onError?: (error: Event) => void
  /** Whether to automatically reconnect on disconnect */
  autoReconnect?: boolean
  /** Whether the stream is enabled */
  enabled?: boolean
}

export type UseFlagStreamReturn = {
  /** Whether the stream is currently connected */
  isConnected: boolean
  /** Manually reconnect to the stream */
  reconnect: () => void
  /** Manually disconnect from the stream */
  disconnect: () => void
}

/**
 * Subscribe to real-time feature flag updates.
 *
 * @example
 * ```tsx
 * const { isConnected } = useFlagStream({
 *   onFlagUpdate: (event) => console.log('Flags changed:', event.flags),
 * })
 * ```
 */
export function useFlagStream(options: UseFlagStreamOptions = {}): UseFlagStreamReturn {
  const { onConnected, onFlagUpdate, onError, autoReconnect = true, enabled = true } = options

  const queryClient = useQueryClient()
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isConnectedRef = useRef(false)
  const mountedRef = useRef(true)

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    isConnectedRef.current = false
  }, [])

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const eventSource = new EventSource("/api/feature-flags/stream")
    eventSourceRef.current = eventSource

    eventSource.addEventListener("connected", (event) => {
      isConnectedRef.current = true
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS // Reset backoff on successful connection
      try {
        const data = JSON.parse((event as MessageEvent).data)
        onConnected?.(data)
      } catch {
        // Ignore parse errors
      }
    })

    eventSource.addEventListener("flag-update", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as FlagUpdateEvent

        // Invalidate TanStack Query cache for affected flags
        // Use partial key matching to invalidate all evaluations for each flag key
        for (const flag of data.flags) {
          queryClient.invalidateQueries({
            queryKey: [...flagQueryKeys.all, "evaluate", flag.key],
          })
        }
        // Also invalidate the list to pick up any metadata changes
        queryClient.invalidateQueries({ queryKey: flagQueryKeys.lists() })

        onFlagUpdate?.(data)
      } catch {
        // Ignore parse errors
      }
    })

    eventSource.addEventListener("heartbeat", () => {
      // Heartbeat received, connection is healthy
    })

    eventSource.onerror = (error) => {
      isConnectedRef.current = false
      onError?.(error)

      // Close the failed connection
      eventSource.close()
      eventSourceRef.current = null

      // Attempt reconnection with exponential backoff
      if (autoReconnect && mountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * BACKOFF_MULTIPLIER,
            MAX_RECONNECT_DELAY_MS,
          )
          connect()
        }, reconnectDelayRef.current)
      }
    }
  }, [enabled, onConnected, onFlagUpdate, onError, autoReconnect, queryClient])

  const reconnect = useCallback(() => {
    disconnect()
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS
    connect()
  }, [disconnect, connect])

  useEffect(() => {
    mountedRef.current = true

    if (enabled) {
      connect()
    }

    return () => {
      mountedRef.current = false
      disconnect()
    }
  }, [enabled, connect, disconnect])

  return {
    isConnected: isConnectedRef.current,
    reconnect,
    disconnect,
  }
}
